<template>
  <section class="auth-panel">
    <h3>Password</h3>
    <p>Reset links go to the staff account's current sign-in email. Passwords do not expire on a schedule.</p>
    <button type="button" :disabled="sending || loading" @click="sendReset">{{ sending ? 'Sending…' : 'Send password reset email' }}</button>
    <p v-if="notice" role="status">{{ notice }}</p>
    <p v-if="error" role="alert">{{ error }}</p>
    <p>Latest tracked reset request: {{ date(summary.lastRequestedAt) }}</p>
    <p>Last password update reported by Firebase: {{ date(summary.passwordUpdatedAt) }}</p>
    <p v-if="summary.verificationAvailable === false" role="alert">Firebase password verification is currently unavailable. Displayed dates may be out of date.</p>

    <AdminEventHistory :uid="uid" category="password" title="View password history" :revision="revision" />
  </section>
  <section class="auth-panel sign-in-panel">
    <h3>Two-factor authentication</h3>
    <p v-if="summary.mfaEnrolled === true">Enabled — Authenticator configured.</p>
    <p v-else-if="summary.mfaEnrolled === false">Setup required.</p>
    <p v-else>{{ loading ? 'Checking authenticator status…' : 'Authenticator status unavailable.' }}</p>
    <router-link v-if="isOwnProfile && summary.mfaEnrolled === false" to="/admin-security">Set up authenticator</router-link>
    <details v-if="summary.canRecoverMfa">
      <summary>Recover lost authenticator</summary>
      <p>This removes the staff member's authenticators and blocks their existing sessions. Verify their identity in person or through a known phone number before proceeding. They must sign in and enroll again.</p>
      <label class="recovery-reason">Recovery reason<textarea v-model="recoveryReason" maxlength="1000" /></label>
      <label class="recovery-verification"><input type="checkbox" v-model="identityVerified" /><span>I independently verified this staff member's identity.</span></label>
      <button type="button" :disabled="recovering || !identityVerified || recoveryReason.trim().length < 10" @click="recover">{{ recovering ? 'Recovering…' : 'Reset authenticator and revoke sessions' }}</button>
    </details>
    <AdminEventHistory :uid="uid" category="recovery" title="View authenticator recovery history" :revision="revision" />
  </section>
  <section class="auth-panel sign-in-panel">
    <h3>Sign-in history</h3>
    <AdminEventHistory :uid="uid" category="signIn" title="View sign-in history" :revision="revision" />
  </section>
</template>

<script>
import AdminEventHistory from './AdminEventHistory.vue';
import { computed, ref, watch } from 'vue';
import { httpsCallable } from 'firebase/functions';
import { projectAuth, projectFunctions } from '@/firebase/firebase';
const listHistory = httpsCallable(projectFunctions, 'listAdminAuthHistory');
const resetAuthenticator = httpsCallable(projectFunctions, 'resetAdminAuthenticator');
const resetPassword = httpsCallable(projectFunctions, 'sendAdminPasswordReset');
export default {
  name: 'AdminAuthHistory',
  components: { AdminEventHistory },
  props: { uid: { type: String, required: true } },
  setup(props) {
    const summary = ref({}), revision = ref(0);
    const isOwnProfile = computed(() => projectAuth.currentUser?.uid === props.uid);
    const loading = ref(false), sending = ref(false), notice = ref(''), error = ref('');
    const recoveryReason = ref(''), identityVerified = ref(false), recovering = ref(false);
    let recoveryId = null;
    let generation = 0;
    const date = value => value ? new Intl.DateTimeFormat('en-US', {dateStyle:'medium',timeStyle:'short',timeZone:'America/Chicago'}).format(new Date(value)) : 'Not recorded';
    const load = async () => {
      if (loading.value) return;
      const current = generation;
      loading.value = true; error.value = '';
      try {
        const {data} = await listHistory({uid:props.uid});
        if (current !== generation) return;
        summary.value = data;
      } catch { if (current === generation) error.value = 'Unable to load authentication history. Check your session and try again.'; }
      finally { if (current === generation) loading.value = false; }
    };
    const sendReset = async () => {
      if (sending.value) return;
      const current = generation;
      sending.value = true; notice.value = ''; error.value = '';
      let failure = '';
      try {
        await resetPassword({uid:props.uid});
        if (current === generation) notice.value = 'The email provider accepted the reset email. The password changes only when the recipient completes the reset.';
      } catch (problem) {
        failure = ['functions/resource-exhausted','functions/failed-precondition','functions/unavailable'].includes(problem.code)
          ? problem.message : 'Unable to request a reset email. Check your session and try again.';
      } finally {
        if (current === generation) {
          sending.value = false;
          await load(); revision.value++;
          if (failure) error.value = failure;
        }
      }
    };
    const recover = async () => {
      if (recovering.value || !identityVerified.value || recoveryReason.value.trim().length < 10) return;
      if (!window.confirm('Reset this staff member’s authenticator and revoke their existing access?')) return;
      const current = generation;
      recovering.value = true; error.value = ''; notice.value = '';
      recoveryId ||= crypto.randomUUID();
      let failure = '';
      try {
        await resetAuthenticator({uid:props.uid,requestId:recoveryId,reason:recoveryReason.value.trim(),identityVerified:identityVerified.value});
        if (current === generation) {
          notice.value = 'Authenticator reset. The staff member must sign in and set up their authenticator again.';
          recoveryReason.value = ''; identityVerified.value = false; recoveryId = null;
        }
      } catch (problem) {
        failure = ['functions/failed-precondition','functions/permission-denied','functions/internal'].includes(problem.code)
          ? problem.message : 'Unable to confirm recovery. Refresh history before retrying.';
      } finally {
        if (current === generation) {
          recovering.value = false; await load(); revision.value++;
          if (failure) error.value = failure;
        }
      }
    };
    watch(() => props.uid, () => {
      generation++; loading.value = false; recoveryId = null; recoveryReason.value = ''; identityVerified.value = false; recovering.value = false; summary.value = {}; notice.value = ''; error.value = ''; sending.value = false;
      void load(false);
    }, {immediate:true});
    return {isOwnProfile,recover,recoveryReason,identityVerified,recovering,summary,revision,loading,sending,notice,error,date,load,sendReset};
  }
};
</script>

<style scoped>
.auth-panel { padding: 20px; border: 1px solid #dce4df; border-radius: 14px; background: #f5f6f5; }
.sign-in-panel { margin-top: 20px; }
h3 { font-size: 1rem; font-weight: 700; }
p, li { font-size: .9rem; overflow-wrap: anywhere; }
button { border: 0; border-radius: 8px; background: #18ab69; color: white; padding: 10px 16px; margin-bottom: 12px; }
button:disabled { opacity: .5; }
summary { cursor: pointer; font-weight: 700; padding: 10px 0; }
.recovery-reason { display: flex; flex-direction: column; gap: 8px; margin-bottom: 16px; }
.recovery-reason textarea { box-sizing: border-box; width: 100%; min-height: 100px; resize: vertical; }
.recovery-verification { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; }
.recovery-verification input[type="checkbox"] { flex: 0 0 auto; width: auto; margin: 0; }
li { margin-bottom: 12px; }
[role="alert"] { color: #a33636; }
</style>
